import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { User, Mail, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ username: '', email: '', new_password: '', confirm_password: '' });

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/admin/verify-identity`, { username: formData.username, email: formData.email });
      toast.success('Identity verified! Set your new password.');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (formData.new_password !== formData.confirm_password) { toast.error('Passwords do not match'); return; }
    if (formData.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/admin/reset-password`, { username: formData.username, email: formData.email, new_password: formData.new_password });
      toast.success('Password reset successful!');
      setTimeout(() => navigate('/admin'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)' }}>
      <Card className="w-full max-w-md shadow-xl border-slate-100">
        <CardHeader className="text-center space-y-3 pb-6">
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Reset Password</CardTitle>
          <CardDescription className="text-base">{step === 1 ? 'Verify your identity' : 'Set new password'}</CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2"><Label className="text-sm font-medium">Username</Label><div className="relative"><User className="absolute left-3 top-3 h-5 w-5 text-slate-400" /><Input data-testid="forgot-username" placeholder="Enter username" className="pl-10 h-12 bg-slate-50" value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} required /></div></div>
              <div className="space-y-2"><Label className="text-sm font-medium">Email</Label><div className="relative"><Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" /><Input data-testid="forgot-email" type="email" placeholder="Enter registered email" className="pl-10 h-12 bg-slate-50" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></div></div>
              <Button type="submit" data-testid="verify-identity-button" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base font-semibold rounded-full" disabled={loading}>{loading ? 'Verifying...' : 'Verify Identity'}</Button>
              <div className="text-center pt-2"><button type="button" onClick={() => navigate('/admin')} className="text-sm text-slate-600 hover:text-slate-900">Back to Login</button></div>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2"><Label className="text-sm font-medium">New Password</Label><div className="relative"><KeyRound className="absolute left-3 top-3 h-5 w-5 text-slate-400" /><Input data-testid="new-password" type="password" placeholder="Min 6 characters" className="pl-10 h-12 bg-slate-50" value={formData.new_password} onChange={(e) => setFormData({...formData, new_password: e.target.value})} required /></div></div>
              <div className="space-y-2"><Label className="text-sm font-medium">Confirm Password</Label><div className="relative"><KeyRound className="absolute left-3 top-3 h-5 w-5 text-slate-400" /><Input data-testid="confirm-password" type="password" placeholder="Re-enter password" className="pl-10 h-12 bg-slate-50" value={formData.confirm_password} onChange={(e) => setFormData({...formData, confirm_password: e.target.value})} required /></div></div>
              <Button type="submit" data-testid="reset-password-button" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base font-semibold rounded-full" disabled={loading}>{loading ? 'Resetting...' : 'Reset Password'}</Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminForgotPassword;
